import React, { useState, useEffect } from 'react';
import './App.css';
import WorkSection from './components/WorkSection';
import AboutSection from './components/AboutSection';
import ContactSection from './components/ContactSection';
import SEOHelmet from './components/SEOHelmet';
import BackToTopButton from './components/BackToTopButton';
import { trackPageView, trackNavigation, trackLanguageChange } from './utils/analytics';
import { parseArtworkPath } from './utils/slugify';

// Import pages if available
let Impressum, Datenschutz;
try {
  Impressum = require('./pages/Impressum').default;
  Datenschutz = require('./pages/Datenschutz').default;
} catch (e) {
  // Will handle missing components
}

function App() {
  // State for language and current page
  const [currentLang, setCurrentLang] = useState('de');
  const [currentPage, setCurrentPage] = useState('home');
  const [activeSection, setActiveSection] = useState('work');
  const [artworkSlug, setArtworkSlug] = useState(null); // For artwork deep linking
  
  // URL paths with localization
  const urlPaths = {
    work: {
      en: 'portfolio',
      de: 'arbeiten'
    },
    about: {
      en: 'about',
      de: 'ueber_mich'
    },
    contact: {
      en: 'contact',
      de: 'kontakt'
    },
    impressum: {
      en: 'imprint',
      de: 'impressum'
    },
    datenschutz: {
      en: 'privacy-policy',
      de: 'datenschutz'
    }
  };
  
  // Convert path to section
  const getPathInfo = (path) => {
    if (!path || path === '/') {
      return { page: 'home', section: 'work', artworkSlug: null };
    }
    
    // Check for artwork paths (/werk/slug or /artwork/slug)
    const artworkInfo = parseArtworkPath(path);
    if (artworkInfo) {
      return { 
        page: 'home', 
        section: 'work', 
        artworkSlug: artworkInfo.slug,
        artworkLang: artworkInfo.language 
      };
    }
    
    const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
    
    // Check for legal pages
    for (const page of ['impressum', 'datenschutz']) {
      if (normalizedPath === urlPaths[page].en || normalizedPath === urlPaths[page].de) {
        return { page: page, section: null, artworkSlug: null };
      }
    }
    
    // Check for content sections
    for (const section of ['work', 'about', 'contact']) {
      if (normalizedPath === urlPaths[section].en || normalizedPath === urlPaths[section].de) {
        return { page: 'home', section: section, artworkSlug: null };
      }
    }
    
    // Default
    return { page: 'home', section: 'work', artworkSlug: null };
  };
  
  // Listen for browser history navigation (back/forward buttons)
  useEffect(() => {
    const handlePopState = (event) => {
      const state = event.state;
      if (state) {
        setCurrentPage(state.page || 'home');
        if (state.section) {
          setActiveSection(state.section);
        }
      } else {
        // Default state when there's no history (initial load)
        handleInitialLoad();
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    // Handle initial page load based on URL path
    handleInitialLoad();
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []); // Only run on component mount
  
  // Function to handle initial page load based on URL path
  const handleInitialLoad = () => {
    const path = window.location.pathname;
    const { page, section, artworkSlug: slug, artworkLang } = getPathInfo(path);
    
    // Detect language from URL path (for all pages, not just artworks)
    const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
    let detectedLang = 'de'; // default
    
    // Check if path matches English or German patterns
    for (const [key, paths] of Object.entries(urlPaths)) {
      if (normalizedPath === paths.en) {
        detectedLang = 'en';
        break;
      } else if (normalizedPath === paths.de) {
        detectedLang = 'de';
        break;
      }
    }
    
    // For artwork pages, use the artworkLang if available
    if (artworkLang) {
      detectedLang = artworkLang;
    }
    
    // Set the detected language before updating page state
    setCurrentLang(detectedLang);
    
    setCurrentPage(page);
    if (section) {
      setActiveSection(section);
    }
    
    // If there's an artwork slug, set it
    if (slug) {
      setArtworkSlug(slug);
      // Track page view but don't change URL for artwork pages
      trackPageView(path);
    } else {
      // Track initial page view
      trackPageView(path || '/');
      
      // Update browser history state but keep the current URL
      // This ensures the canonical URL matches what the user requested
      window.history.replaceState(
        { page: page, section: section }, 
        '', 
        path || '/'
      );
    }
  };
  
  // Helper to generate clean URLs
  const getUrlForState = (page, section = null) => {
    if (page === 'home') {
      // Work section (main gallery) should stay at root URL for SEO
      if (section === 'work' || !section) {
        return '/';
      }
      // Other sections get their localized paths
      return `/${urlPaths[section][currentLang]}`;
    } else {
      return `/${urlPaths[page][currentLang]}`;
    }
  };

  // Function to handle language switching
  const toggleLanguage = () => {
    setCurrentLang(prevLang => {
      const newLang = prevLang === 'en' ? 'de' : 'en';
      
      // Track language change
      trackLanguageChange(newLang);
      
      // Update URL to reflect new language but same content
      const newUrl = getUrlForState(currentPage, currentPage === 'home' ? activeSection : null);
      window.history.replaceState(
        { page: currentPage, section: currentPage === 'home' ? activeSection : null }, 
        '', 
        newUrl
      );
      
      return newLang;
    });
  };

  // Updated navigation function with clean URLs
  const navigateToSection = (page, section) => {
    setCurrentPage(page);
    
    if (page === 'home') {
      setActiveSection(section || 'work');
      
      // Track navigation
      const path = getUrlForState('home', section);
      trackPageView(path);
      trackNavigation(section || 'work');
      
      // Add a small delay for DOM elements to update
      setTimeout(() => {
        const sections = document.querySelectorAll('.section-content');
        sections.forEach(sectionEl => {
          if (sectionEl.id === section) {
            sectionEl.classList.remove('hidden');
          } else {
            sectionEl.classList.add('hidden');
          }
        });
        
        // Update browser history with clean URL
        window.history.pushState(
          { page: 'home', section: section }, 
          '', 
          getUrlForState('home', section)
        );
      }, 10);
    } else {
      // Track navigation to legal pages
      const path = getUrlForState(page);
      trackPageView(path);
      trackNavigation(page);
      
      // Update browser history for legal pages with clean URL
      window.history.pushState(
        { page: page }, 
        '', 
        getUrlForState(page)
      );
    }
    
    window.scrollTo(0, 0);
  };

  // Get SEO data for current page/section
  const getSEOData = () => {
    // Base SEO content
    const content = {
      home: {
        work: {
          en: {
            title: 'Art Showcase - Professional Artist Portfolio',
            description: 'Explore a curated collection of fine art paintings showcasing a unique artistic style that blends abstract and impressionist influences with natural themes.',
            pageType: 'CollectionPage',
            keywords: 'art portfolio, art collection, paintings, artist showcase, fine art, abstract art'
          },
          de: {
            title: 'atelier wpb - Professionelles Künstlerportfolio',
            description: 'Entdecken Sie eine kuratierte Sammlung von Gemälden, die einen einzigartigen künstlerischen Stil zeigen, der abstrakte und impressionistische Einflüsse mit natürlichen Themen verbindet.',
            pageType: 'CollectionPage',
            keywords: 'kunstportfolio, kunstsammlung, gemälde, künstlerpräsentation, bildende kunst, abstrakte kunst'
          }
        },
        about: {
          en: {
            title: 'About the Artist - Art Portfolio',
            description: 'Learn more about the artist behind the showcased artwork - education, artistic philosophy, exhibitions, and professional experience.',
            pageType: 'ProfilePage',
            keywords: 'artist biography, art education, art exhibitions, artist experience, professional artist'
          },
          de: {
            title: 'Über den Künstler - atelier wpb Portfolio',
            description: 'Erfahren Sie mehr über den Künstler hinter der atelier wpb - Ausbildung, künstlerische Philosophie, Ausstellungen und berufliche Erfahrung.',
            pageType: 'ProfilePage',
            keywords: 'künstlerbiografie, kunstausbildung, kunstausstellungen, künstlererfahrung, professioneller künstler'
          }
        },
        contact: {
          en: {
            title: 'Contact the Artist - Art Portfolio',
            description: 'Get in touch with the artist for commissions, exhibitions, or inquiries about artwork purchases.',
            pageType: 'ContactPage',
            keywords: 'contact artist, art commission, buy artwork, art inquiries, artist contact information'
          },
          de: {
            title: 'Kontakt zum Künstler - atelier wpb',
            description: 'Nehmen Sie Kontakt mit dem Künstler auf für Aufträge, Ausstellungen oder Anfragen zu Kunstwerkkäufen.',
            pageType: 'ContactPage',
            keywords: 'künstler kontaktieren, kunstauftrag, kunstwerk kaufen, kunstanfragen, künstler kontaktinformationen'
          }
        }
      },
      impressum: {
        en: {
          title: 'Imprint - Art Showcase',
          description: 'Legal information about the Art Showcase website including contact details and responsible parties.',
          pageType: 'AboutPage',
          keywords: 'imprint, legal information, contact details, art showcase legal'
        },
        de: {
          title: 'Impressum - atelier wpb',
          description: 'Rechtliche Informationen über die atelier wpb-Website einschließlich Kontaktdaten und verantwortliche Parteien.',
          pageType: 'AboutPage',
          keywords: 'impressum, rechtliche informationen, kontaktdaten, kunstausstellung rechtlich'
        }
      },
      datenschutz: {
        en: {
          title: 'Privacy Policy - Art Showcase',
          description: 'Information about how the Art Showcase website collects, processes, and protects user data.',
          pageType: 'AboutPage',
          keywords: 'privacy policy, data protection, user data, cookies, art showcase privacy'
        },
        de: {
          title: 'Datenschutzerklärung - atelier wpb',
          description: 'Informationen darüber, wie die atelier wpb-Website Benutzerdaten sammelt, verarbeitet und schützt.',
          pageType: 'AboutPage',
          keywords: 'datenschutzerklärung, datenschutz, benutzerdaten, cookies, atelier wpb datenschutz'
        }
      }
    };
    
    // Determine the path for canonical URL (use localized paths)
    let path = '/';
    if (currentPage === 'home') {
      if (activeSection === 'work') {
        path = `/${urlPaths.work[currentLang]}`;
      } else {
        path = `/${urlPaths[activeSection][currentLang]}`;
      }
    } else if (currentPage !== 'home') {
      path = `/${urlPaths[currentPage][currentLang]}`;
    }
    
    // Get page-specific data
    if (currentPage === 'home') {
      // Ensure activeSection exists in content before accessing
      const validSection = content.home[activeSection] ? activeSection : 'work';
      const sectionData = content.home[validSection][currentLang];
      return {
        ...sectionData,
        path: path,
        language: currentLang
      };
    } else {
      const pageData = content[currentPage][currentLang];
      return {
        ...pageData,
        path: path,
        language: currentLang
      };
    }
  };

  // Homepage content
  const renderHomePage = () => {
    return (
      <>
        {/* Header & Navigation */}
        <header>
          <div className="container header-container">
            <button 
              className="logo-button logo-font" 
              onClick={() => navigateToSection('home', 'work')}
            >
              {currentLang === 'en' ? 'atelier-wpb' : 'atelier-wpb'}
            </button>
            <nav>
              <ul>
                <li>
                  <a 
                    href={`/${urlPaths.work[currentLang]}`} 
                    className={`nav-link ${activeSection === 'work' ? 'active' : ''}`} 
                    onClick={(e) => { e.preventDefault(); navigateToSection('home', 'work'); }}
                  >
                    {currentLang === 'en' ? 'My Work' : 'Meine Arbeiten'}
                  </a>
                </li>
                <li>
                  <a 
                    href={`/${urlPaths.about[currentLang]}`} 
                    className={`nav-link ${activeSection === 'about' ? 'active' : ''}`} 
                    onClick={(e) => { e.preventDefault(); navigateToSection('home', 'about'); }}
                  >
                    {currentLang === 'en' ? 'About' : 'Über Mich'}
                  </a>
                </li>
                <li>
                  <a 
                    href={`/${urlPaths.contact[currentLang]}`} 
                    className={`nav-link ${activeSection === 'contact' ? 'active' : ''}`} 
                    onClick={(e) => { e.preventDefault(); navigateToSection('home', 'contact'); }}
                  >
                    {currentLang === 'en' ? 'Contact' : 'Kontakt'}
                  </a>
                </li>
              </ul>
              <button className="language-switch" onClick={toggleLanguage}>
                {currentLang === 'en' ? 'DE' : 'EN'}
              </button>
            </nav>
          </div>
        </header>

        {/* Content Sections */}
        <WorkSection 
          language={currentLang} 
          isVisible={activeSection === 'work'}
          initialArtworkSlug={artworkSlug}
        />
        
        <AboutSection 
          language={currentLang} 
          isVisible={activeSection === 'about'}
        />
        
        <ContactSection 
          language={currentLang} 
          isVisible={activeSection === 'contact'}
        />
      </>
    );
  };

  // Render current page content
  const renderPageContent = () => {
    switch (currentPage) {
      case 'impressum':
        return Impressum ? 
          <Impressum 
            language={currentLang} 
            toggleLanguage={toggleLanguage} 
            navigateTo={navigateToSection} // Pass navigateToSection instead of navigateTo
          /> : 
          <div>Impressum page not found</div>;
      case 'datenschutz':
        return Datenschutz ? 
          <Datenschutz 
            language={currentLang} 
            toggleLanguage={toggleLanguage} 
            navigateTo={navigateToSection} // Pass navigateToSection instead of navigateTo
          /> : 
          <div>Datenschutz page not found</div>;
      default:
        return renderHomePage();
    }
  };

  const seoData = getSEOData();

  return (
    <>
      <SEOHelmet 
        pageType={seoData.pageType}
        title={seoData.title}
        description={seoData.description}
        path={seoData.path}
        language={seoData.language}
        keywords={seoData.keywords}
      />
      
      {renderPageContent()}
      
      {/* Back to Top Button */}
      <BackToTopButton />

      {/* Footer with Links */}
      <footer>
        <div className="container footer-container">
          <div className="footer-info">
            <p>&copy; 2025 {currentLang === 'en' ? 'atelier-wpb' : 'atelier-wpb'}. {currentLang === 'en' ? 'All rights reserved.' : 'Alle Rechte vorbehalten.'}</p>
            <div className="legal-links">
              <button 
                className="link-button" 
                onClick={() => navigateToSection('impressum')}
              >
                {currentLang === 'en' ? 'Imprint' : 'Impressum'}
              </button> | 
              <button 
                className="link-button" 
                onClick={() => navigateToSection('datenschutz')}
              >
                {currentLang === 'en' ? 'Privacy Policy' : 'Datenschutzerklärung'}
              </button>
            </div>
          </div>
          <div className="social-links">
            <a href="https://www.instagram.com/atelier_wpb/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
              </svg>
            </a>
            <a href="https://www.facebook.com/people/Atelier-WPB/61586615890951/" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
              </svg>
            </a>
            <button onClick={() => navigateToSection('home', 'contact')} className="link-button social-button" aria-label={currentLang === 'en' ? 'Contact via Email' : 'Kontakt per E-Mail'}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2 2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
            </button>
          </div>
        </div>
      </footer>
    </>
  );
}

export default App;
